import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlbumEditorActionRow,
  AlbumEditorCheckbox,
  AlbumEditorCheckboxLabel,
  AlbumEditorDangerButton,
  AlbumEditorFieldGrid,
  AlbumEditorFieldGroup,
  AlbumEditorFieldLabel,
  AlbumEditorFormCard,
  AlbumEditorLayout,
  AlbumEditorPrimaryButton,
  AlbumEditorReadOnlySection,
  AlbumEditorReadOnlyTitle,
  AlbumEditorSecondaryLink,
  AlbumEditorSectionBody,
  AlbumEditorSectionHeader,
  AlbumEditorSectionTitle,
  AlbumEditorSongListAction,
  AlbumEditorSongList,
  AlbumEditorSongListControls,
  AlbumEditorSongListItem,
  AlbumEditorSongListLink,
  AlbumEditorSongOrderButton,
  AlbumEditorStatusText,
  AlbumEditorTextInput,
} from '../components/styled';
import { useLibraryApi } from '../context/BandContext';
import { api } from '../lib/api';

function AlbumEditorPage() {
  const navigate = useNavigate();
  const { albumId = '', bandId = '' } = useParams();
  const libraryApi = useLibraryApi();
  const pathPrefix = bandId ? `/bands/${bandId}` : '';
  const [searchParams] = useSearchParams();
  const isEditing = Boolean(albumId);
  const sourceQuery = searchParams.get('albumsQ') || searchParams.get('q') || '';
  const sourceAlphaRange =
    searchParams.get('albumsAlpha') || searchParams.get('alpha') || '';
  const albumsRouteParams = new URLSearchParams();

  if (sourceQuery) {
    albumsRouteParams.set('q', sourceQuery);
  }

  if (sourceAlphaRange) {
    albumsRouteParams.set('alpha', sourceAlphaRange);
  }

  if (bandId) {
    albumsRouteParams.set('scope', 'band');
    albumsRouteParams.set('bandId', bandId);
  }

  const albumsRoute = albumsRouteParams.toString()
    ? `/albums?${albumsRouteParams.toString()}`
    : '/albums';
  const albumViewRouteParams = new URLSearchParams();

  if (sourceQuery) {
    albumViewRouteParams.set('albumsQ', sourceQuery);
  }

  if (sourceAlphaRange) {
    albumViewRouteParams.set('albumsAlpha', sourceAlphaRange);
  }

  if (bandId) {
    albumViewRouteParams.set('scope', 'band');
    albumViewRouteParams.set('bandId', bandId);
  }

  const albumViewRoute = albumViewRouteParams.toString()
    ? `/library/albums/${albumId}?${albumViewRouteParams.toString()}`
    : `/library/albums/${albumId}`;
  const [formState, setFormState] = useState({
    title: '',
    artist: '',
    flag: false,
  });
  const [albumSongs, setAlbumSongs] = useState([]);
  const [bands, setBands] = useState([]);
  const [selectedBandIds, setSelectedBandIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingRemoveSongId, setPendingRemoveSongId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let isActive = true;

    const loadAlbum = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        if (!isEditing) {
          const bandsResponse = await api.getBands();
          if (isActive) {
            setBands((bandsResponse?.bands || []).sort((left, right) => (left.name || '').localeCompare(right.name || '')));
            setFormState({
              title: '',
              artist: '',
              flag: false,
            });
            setAlbumSongs([]);
            setSelectedBandIds(bandId ? [bandId] : []);
            setIsLoading(false);
          }

          return;
        }

        const [data, bandsResponse] = await Promise.all([libraryApi.getAlbums(), api.getBands()]);

        if (!isActive) {
          return;
        }

        const album = (data?.albums || []).find(
          (currentAlbum) => currentAlbum._id === albumId
        );
        setBands((bandsResponse?.bands || []).sort((left, right) => (left.name || '').localeCompare(right.name || '')));

        if (!album) {
          setErrorMessage('Album not found.');
          setAlbumSongs([]);
        } else {
          setFormState({
            title: album.title || '',
            artist: album.artist || '',
            flag: Boolean(album.flag),
          });
          setAlbumSongs(Array.isArray(album.songs) ? album.songs : []);
          setSelectedBandIds([...new Set([
            ...(album.bandId ? [album.bandId] : []),
            ...(album.sharedBandIds || []),
          ].map((id) => id.toString()))]);
        }
      } catch (error) {
        if (isActive && error.status !== 401) {
          setErrorMessage(error.message || 'Failed to load album.');
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadAlbum();

    return () => {
      isActive = false;
    };
  }, [albumId, isEditing, libraryApi, bandId]);

  const handleFieldChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormState((currentState) => ({
      ...currentState,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setErrorMessage('');

    const payload = {
      title: formState.title.trim(),
      artist: formState.artist.trim() || undefined,
      flag: formState.flag,
      songs: albumSongs.map((song) => song._id),
      sharedBandIds: selectedBandIds,
    };

    try {
      if (isEditing) {
        await libraryApi.updateAlbum(albumId, payload);
      } else {
        await libraryApi.saveAlbum(payload);
      }

      navigate(albumsRoute);
    } catch (error) {
      if (error.status !== 401) {
        setErrorMessage(error.message || 'Failed to save album.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAlbum = async () => {
    if (
      typeof window !== 'undefined' &&
      !window.confirm(`Delete "${formState.title || 'this album'}"?`)
    ) {
      return;
    }

    setIsDeleting(true);
    setErrorMessage('');

    try {
      await libraryApi.deleteAlbum(albumId);
      navigate(albumsRoute);
    } catch (error) {
      if (error.status !== 401) {
        setErrorMessage(error.message || 'Failed to delete album.');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRemoveSongFromAlbum = async (song) => {
    if (!isEditing || !song?._id || pendingRemoveSongId) {
      return;
    }

    if (
      typeof window !== 'undefined' &&
      !window.confirm(`Remove "${song.title || 'this song'}" from this album?`)
    ) {
      return;
    }

    setPendingRemoveSongId(song._id);
    setErrorMessage('');

    try {
      const remainingSongIds = albumSongs
        .filter((currentSong) => currentSong._id !== song._id)
        .map((currentSong) => currentSong._id);

      await libraryApi.updateAlbum(albumId, {
        songs: remainingSongIds,
      });

      setAlbumSongs((currentSongs) =>
        currentSongs.filter((currentSong) => currentSong._id !== song._id)
      );
    } catch (error) {
      if (error.status !== 401) {
        setErrorMessage(error.message || 'Failed to remove song from album.');
      }
    } finally {
      setPendingRemoveSongId('');
    }
  };

  const handleMoveSong = (songIndex, direction) => {
    setAlbumSongs((currentSongs) => {
      const nextIndex = songIndex + direction;

      if (nextIndex < 0 || nextIndex >= currentSongs.length) {
        return currentSongs;
      }

      const nextSongs = [...currentSongs];
      const [songToMove] = nextSongs.splice(songIndex, 1);
      nextSongs.splice(nextIndex, 0, songToMove);

      return nextSongs;
    });
  };

  return (
    <AlbumEditorLayout>
      <AlbumEditorFormCard onSubmit={handleSubmit}>
        <AlbumEditorSectionHeader>
          <AlbumEditorSectionTitle>
            {isEditing ? 'Edit Album' : 'New Album'}
          </AlbumEditorSectionTitle>
          <AlbumEditorSectionBody>
            Album details are managed here. Remove songs from this album below;
            add songs from the song editor.
          </AlbumEditorSectionBody>
        </AlbumEditorSectionHeader>

        {isLoading ? <AlbumEditorStatusText>Loading album...</AlbumEditorStatusText> : null}
        {errorMessage ? (
          <AlbumEditorStatusText $error>{errorMessage}</AlbumEditorStatusText>
        ) : null}

        {!isLoading ? (
          <>
            <AlbumEditorFieldGrid>
              <AlbumEditorFieldGroup>
                <AlbumEditorFieldLabel htmlFor="title">Title</AlbumEditorFieldLabel>
                <AlbumEditorTextInput
                  id="title"
                  name="title"
                  value={formState.title}
                  onChange={handleFieldChange}
                  placeholder="Album title"
                  required
                />
              </AlbumEditorFieldGroup>

              <AlbumEditorFieldGroup>
                <AlbumEditorFieldLabel htmlFor="artist">Artist</AlbumEditorFieldLabel>
                <AlbumEditorTextInput
                  id="artist"
                  name="artist"
                  value={formState.artist}
                  onChange={handleFieldChange}
                  placeholder="Album artist"
                />
              </AlbumEditorFieldGroup>
            </AlbumEditorFieldGrid>

            <AlbumEditorFieldGroup>
              <AlbumEditorFieldLabel htmlFor="flag">Flag</AlbumEditorFieldLabel>
              <AlbumEditorCheckboxLabel htmlFor="flag">
                <AlbumEditorCheckbox
                  id="flag"
                  name="flag"
                  type="checkbox"
                  checked={formState.flag}
                  onChange={handleFieldChange}
                />
                Mark this album as flagged
              </AlbumEditorCheckboxLabel>
            </AlbumEditorFieldGroup>

            <AlbumEditorFieldGroup>
              <AlbumEditorFieldLabel>Share with bands</AlbumEditorFieldLabel>
              {bands.length ? bands.map((band) => (
                <AlbumEditorCheckboxLabel key={band._id} htmlFor={`album-band-${band._id}`}>
                  <AlbumEditorCheckbox
                    id={`album-band-${band._id}`}
                    type="checkbox"
                    checked={selectedBandIds.includes(band._id)}
                    onChange={() => setSelectedBandIds((ids) => ids.includes(band._id) ? ids.filter((id) => id !== band._id) : [...ids, band._id])}
                    disabled={isSaving || isDeleting || bandId === band._id}
                  />
                  {band.name}
                </AlbumEditorCheckboxLabel>
              )) : <AlbumEditorStatusText>No bands are available.</AlbumEditorStatusText>}
            </AlbumEditorFieldGroup>

            {isEditing ? (
              <AlbumEditorReadOnlySection>
                <AlbumEditorReadOnlyTitle>Current Songs</AlbumEditorReadOnlyTitle>
                {albumSongs.length > 0 ? (
                  <AlbumEditorSongList>
                    {albumSongs.map((song, index) => (
                      <AlbumEditorSongListItem key={song._id}>
                        <AlbumEditorSongListLink
                          as={Link}
                          to={`${pathPrefix}/songs/${song._id}/edit`}
                        >
                          {song.title || 'Untitled song'}
                          {song.artist ? ` · ${song.artist}` : ''}
                        </AlbumEditorSongListLink>
                        <AlbumEditorSongListControls>
                          <AlbumEditorSongOrderButton
                            type="button"
                            onClick={() => handleMoveSong(index, -1)}
                            disabled={
                              index === 0 ||
                              isSaving ||
                              isDeleting ||
                              Boolean(pendingRemoveSongId)
                            }
                          >
                            Up
                          </AlbumEditorSongOrderButton>
                          <AlbumEditorSongOrderButton
                            type="button"
                            onClick={() => handleMoveSong(index, 1)}
                            disabled={
                              index === albumSongs.length - 1 ||
                              isSaving ||
                              isDeleting ||
                              Boolean(pendingRemoveSongId)
                            }
                          >
                            Down
                          </AlbumEditorSongOrderButton>
                        </AlbumEditorSongListControls>
                        <AlbumEditorSongListAction
                          type="button"
                          onClick={() => handleRemoveSongFromAlbum(song)}
                          disabled={
                            isSaving ||
                            isDeleting ||
                            Boolean(pendingRemoveSongId)
                          }
                        >
                          {pendingRemoveSongId === song._id
                            ? 'Removing...'
                            : 'Remove'}
                        </AlbumEditorSongListAction>
                      </AlbumEditorSongListItem>
                    ))}
                  </AlbumEditorSongList>
                ) : (
                  <AlbumEditorStatusText>
                    No songs are currently assigned to this album.
                  </AlbumEditorStatusText>
                )}
              </AlbumEditorReadOnlySection>
            ) : null}

            <AlbumEditorPrimaryButton
              type="submit"
              disabled={isSaving || isDeleting || Boolean(pendingRemoveSongId)}
            >
              {isSaving
                ? isEditing
                  ? 'Saving Album...'
                  : 'Creating Album...'
                : isEditing
                  ? 'Save Album'
                  : 'Create Album'}
            </AlbumEditorPrimaryButton>

            <AlbumEditorActionRow>
              {isEditing ? (
                <AlbumEditorSecondaryLink as={Link} to={albumViewRoute}>
                  View Album
                </AlbumEditorSecondaryLink>
              ) : null}

              <AlbumEditorSecondaryLink as={Link} to={albumsRoute}>
                Back to Albums
              </AlbumEditorSecondaryLink>

              {isEditing ? (
                <AlbumEditorDangerButton
                  type="button"
                  onClick={handleDeleteAlbum}
                  disabled={isSaving || isDeleting || Boolean(pendingRemoveSongId)}
                >
                  {isDeleting ? 'Deleting Album...' : 'Delete Album'}
                </AlbumEditorDangerButton>
              ) : null}
            </AlbumEditorActionRow>
          </>
        ) : null}
      </AlbumEditorFormCard>
    </AlbumEditorLayout>
  );
}

export default AlbumEditorPage;
